import React, { useState, useEffect, createContext, useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { authAPI } from '../services/api'
import toast from 'react-hot-toast'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  // Vérifier l'authentification au chargement
  const { data: userData, isLoading: userLoading } = useQuery(
    'auth-user',
    authAPI.getMe,
    {
      retry: false,
      onError: () => {
        // Supprimer le token s'il est invalide
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        setUser(null)
      }
    }
  )

  useEffect(() => {
    if (!userLoading) {
      setUser(userData?.user || null)
      setLoading(false)
    }
  }, [userData, userLoading])

  // Mutation pour la connexion
  const loginMutation = useMutation(authAPI.login, {
    onSuccess: (response) => {
      const { user: userInfo, tokens } = response.data
      localStorage.setItem('accessToken', tokens.accessToken)
      localStorage.setItem('refreshToken', tokens.refreshToken)
      setUser(userInfo)
      queryClient.setQueryData('auth-user', { user: userInfo })
      toast.success('Connexion réussie !')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erreur de connexion')
    }
  })

  // Mutation pour l'inscription
  const registerMutation = useMutation(authAPI.register, {
    onSuccess: (response) => {
      const { user: userInfo, tokens } = response.data
      localStorage.setItem('accessToken', tokens.accessToken)
      localStorage.setItem('refreshToken', tokens.refreshToken)
      setUser(userInfo)
      queryClient.setQueryData('auth-user', { user: userInfo })
      toast.success('Compte créé avec succès !')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erreur lors de la création du compte')
    }
  })

  // Mutation pour la déconnexion
  const logoutMutation = useMutation(authAPI.logout, {
    onSuccess: () => {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      setUser(null)
      queryClient.clear()
      toast.success('Déconnexion réussie')
    },
    onError: () => {
      // Déconnexion même en cas d'erreur
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      setUser(null)
      queryClient.clear()
    }
  })

  const login = (credentials) => {
    return loginMutation.mutateAsync(credentials)
  }

  const register = (userData) => {
    return registerMutation.mutateAsync(userData)
  }

  const logout = () => {
    return logoutMutation.mutateAsync()
  }

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isStudent: user?.role === 'student'
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
